"use client";

import * as stylex from "@stylexjs/stylex";
import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import type { Breakpoint } from "@/domain/breakpoint";
import "@/theme/tokens.stylex";
import { colors, motion } from "@/theme/tokens.stylex";
import { dialogKeyDown, useFocusOnOpen } from "./dialogFocus";
import { useFieldInView } from "./fieldInView";
type PageProps = {
  label: string;
  breakpoint: Breakpoint;
  onEscape: () => void;
  leaving?: boolean;
  onLeft: () => void;
  keyboardInset?: number;
  topBar?: ReactNode;
  children: ReactNode;
};
type Direction = "in" | "out";
const SLIDE_MS = 280;
const PADDING: Record<Breakpoint, number> = {
  large: 48,
  normal: 24,
  small: 12
};
const SLIDE_IN_EASING = "cubic-bezier(.2,.7,.2,1)";
const SLIDE_OUT_EASING = "cubic-bezier(.6,0,.8,.3)";
const _temp = {
  kGO01o: "xjqpyvm",
  "$$css": true
};
const styles = {
  root: {
    kVAEAm: "xixxii4",
    k87sOh: "x13vifvy",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    krVfgx: "x1ey2m1c",
    kY2c9j: "x1355qak",
    kXHlph: "x6ikm8r",
    kORKVm: "x1odjw0f",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kI3sdo: "x1a2a7pz",
    k1ekBW: "x1ebb0lf",
    kIyJzY: "x1fmhw06",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  content: {
    kzQI83: "x1iyjqo2",
    kmuXW: "x2lah0s",
    kCS8Yb: "xdl72j9",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    $$css: true
  },
  aboveKeyboard: (padding: number, inset: number) => [_temp, {
    "--x-paddingBottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(padding + inset)
  }],
  leaving: {
    kfzvcC: "x47corl",
    $$css: true
  }
};
const sizes = {
  large: {
    kOIVth: "x1665zp3",
    kaPefq: "x1vz1eyh",
    $$css: true
  },
  normal: {
    kOIVth: "xou54vl",
    kaPefq: "x1vz1eyh",
    $$css: true
  },
  small: {
    kOIVth: "x1v2ro7d",
    kaPefq: "x1vz1eyh",
    $$css: true
  }
};
const contentSizes = {
  large: {
    kg3NbH: "x1sdxjpb",
    kOIVth: "x1665zp3",
    $$css: true
  },
  normal: {
    kg3NbH: "x4l9tsp",
    kOIVth: "xou54vl",
    $$css: true
  },
  small: {
    kg3NbH: "xaope02",
    kOIVth: "x1v2ro7d",
    $$css: true
  }
};
export function Page({
  label,
  breakpoint,
  onEscape,
  leaving = false,
  onLeft,
  keyboardInset = 0,
  topBar,
  children
}: PageProps) {
  const page = useRef<HTMLDivElement>(null);
  useFocusOnOpen(page);
  useSlide(page, leaving, onLeft);
  const keyboardPadding = useFieldInView(page, keyboardInset);
  return <div ref={page} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onKeyDown={dialogKeyDown(onEscape)} {...stylex.props(styles.root, sizes[breakpoint], styles.aboveKeyboard(PADDING[breakpoint], keyboardPadding), leaving && styles.leaving)}>
      {topBar}
      <div {...stylex.props(styles.content, contentSizes[breakpoint])}>{children}</div>
    </div>;
}
function useSlide(page: RefObject<HTMLElement | null>, leaving: boolean, onLeft: () => void) {
  const left = useRef(onLeft);
  left.current = onLeft;
  useEffect(() => {
    if (page.current) slide(page.current, "in");
  }, [page]);
  useEffect(() => {
    if (!leaving || !page.current) return;
    const animation = slide(page.current, "out");
    if (!animation) return left.current();
    let live = true;
    void animation.finished.catch(() => {}).then(() => {
      if (live) left.current();
    });
    return () => {
      live = false;
    };
  }, [page, leaving]);
}
function slide(page: HTMLElement, direction: Direction): Animation | undefined {
  if (!page.animate || reducedMotion()) return undefined;
  const offscreen = "translateX(100%)";
  const frames = direction === "in" ? [offscreen, "none"] : ["none", offscreen];
  return page.animate({
    transform: frames
  }, {
    duration: SLIDE_MS,
    easing: direction === "in" ? SLIDE_IN_EASING : SLIDE_OUT_EASING,
    fill: "forwards"
  });
}
function reducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}